import React from "react";
import type { GetRef } from "antd";

import { Button, Divider, Flex, Select, SelectProps, Typography } from "antd";
import { debounce } from "lodash";
import qs from "qs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { DICTIONARY_API_LOCATIONS_CONTROLLER_END_POINTS } from "../../../config/apiEndPoints/DictionaryApiEndsPoints/LocationsController";
import { SERVER_ADDRESSES } from "../../../config/serverAddressesConfig";
import { LocationDto } from "../../../dto/Location/LocationDto";
import LocationModel from "../../../viewModels/Location/LocationModel";
import { makeSelectLanguage } from "../../../containers/App/selectors";
import getLocalizedName from "../../../utils/getLocalizedName";
import LocationResolutionModal from "../../LocationResolutionModal";
import { getIconByLocationType } from "./locationIcons";

export { getIconByLocationType } from "./locationIcons";

const locationPath = `${SERVER_ADDRESSES.DICTIONARY_API}/${DICTIONARY_API_LOCATIONS_CONTROLLER_END_POINTS.CONTROLLER_PATH}${DICTIONARY_API_LOCATIONS_CONTROLLER_END_POINTS.BASIC_INFO}`;

export type LocationModelOption = {
  label: string;
  value: number;
  data: LocationModel;
};

async function fetchLocations({
  pattern,
  filterPredicate,
}: {
  pattern: string;
  filterPredicate?: (location: LocationModel) => boolean;
}): Promise<Array<LocationDto>> {
  const queryStr = qs.stringify({ pattern }, { addQueryPrefix: true });
  const response = await fetch(`${locationPath}${queryStr}`);
  if (!response.ok) throw new Error("Location search failed");
  let locations: Array<LocationDto> = await response.json();

  if (filterPredicate) {
    locations = locations.filter(filterPredicate);
  }

  return locations;
}

type CommonProps = Pick<SelectProps, "id" | "aria-label" | "aria-describedby" | "aria-required" | "onBlur" | "onFocus"> & {
  placeholder?: React.ReactNode;
  filterPredicate?: (location: LocationModel) => boolean;
  disabled?: boolean;
  allowCreateNewLocation?: boolean;
  size?: SelectProps["size"];
  allowClear?: SelectProps["allowClear"];
  ["aria-invalid"]?: SelectProps["aria-invalid"];
};

type Props = CommonProps &
  (
    | { mode?: undefined; value?: LocationModel; onChange?: (value?: LocationModel) => void }
    | { mode: "multiple"; value?: Array<LocationModel>; onChange?: (value: Array<LocationModel>) => void }
  );

const LocationPicker = React.memo(
  React.forwardRef<GetRef<typeof Select>, Props>(function LocationPicker(props, ref) {
    const {
      mode,
      value,
      onChange: _onChange,
      placeholder,
      filterPredicate,
      disabled,
      allowCreateNewLocation,
      size,
      allowClear,
      ...selectProps
    } = props;
    const multiple = mode === "multiple";
    const { t } = useTranslation("translations");
    const lang = useSelector(makeSelectLanguage());

    const [fetching, setFetching] = React.useState(false);
    const [searchFailed, setSearchFailed] = React.useState(false);
    const [options, setOptions] = React.useState<Array<LocationModelOption>>([]);
    const fetchRef = React.useRef(0);
    const [addLocationModalOpen, setAddLocationModalOpen] = React.useState(false);
    const [lastSearchQuery, setLastSearchQuery] = React.useState("");

    const debounceFetcher = React.useMemo(() => {
      const loadOptions = (name: string) => {
        fetchRef.current += 1;
        const fetchId = fetchRef.current;
        setFetching(true);
        setSearchFailed(false);

        fetchLocations({ pattern: name, filterPredicate })
          .then((newOptions) => {
            if (fetchId !== fetchRef.current) {
              return;
            }

            setOptions(
              newOptions
                .filter((option) => option.type !== "UNKNOWN")
                .map((option) => ({
                  value: option.id,
                  label: getLocalizedName(option, lang),
                  data: option,
                })),
            );
          })
          .catch(() => {
            if (fetchId === fetchRef.current) {
              setOptions([]);
              setSearchFailed(true);
            }
          })
          .finally(() => {
            if (fetchId === fetchRef.current) {
              setFetching(false);
            }
          });
      };

      return debounce(loadOptions, 300);
    }, [filterPredicate, lang]);

    React.useEffect(() => () => debounceFetcher.cancel(), [debounceFetcher]);

    const selectedLocations = React.useMemo(() => (multiple ? value ?? [] : value ? [value] : []), [multiple, value]);
    const selectOptions = React.useMemo(() => {
      const locationsById = new Map(options.map((option) => [option.value, option.data]));
      selectedLocations.forEach((location) => {
        if (!locationsById.has(location.id)) locationsById.set(location.id, location);
      });
      return Array.from(locationsById.values()).map((location) => ({
        value: location.id,
        label: getLocalizedName(location, lang),
        data: location,
      }));
    }, [lang, options, selectedLocations]);

    const handleChange = (
      selectedValue?: LocationModelOption | Array<LocationModelOption>,
      option?: LocationModelOption | Array<LocationModelOption>,
    ) => {
      const locationsById = new Map<number, LocationModel>([
        ...selectedLocations.map((location) => [location.id, location] as const),
        ...selectOptions.map((item) => [item.value, item.data] as const),
      ]);
      const resolveLocation = (item?: LocationModelOption, selectedOption?: LocationModelOption) =>
        selectedOption?.data ?? item?.data ?? (item?.value !== undefined ? locationsById.get(item.value) : undefined);

      if (props.mode === "multiple") {
        const selectedValues = Array.isArray(selectedValue) ? selectedValue : [];
        const selectedOptions = Array.isArray(option) ? option : [];
        props.onChange?.(
          selectedValues
            .map((item, index) => resolveLocation(item, selectedOptions[index]))
            .filter((location): location is LocationModel => location !== undefined),
        );
      } else {
        props.onChange?.(
          resolveLocation(Array.isArray(selectedValue) ? undefined : selectedValue, Array.isArray(option) ? undefined : option),
        );
      }
    };

    const handleAddLocation = React.useCallback(
      (location: LocationDto) => {
        if (props.mode === "multiple") {
          const currentValue = props.value ?? [];
          props.onChange?.(currentValue.some((item) => item.id === location.id) ? currentValue : [...currentValue, location]);
        } else {
          props.onChange?.(location);
        }
        setAddLocationModalOpen(false);
      },
      [props],
    );

    const selectValue = multiple
      ? (value ?? []).map((location) => ({ label: getLocalizedName(location, lang), value: location.id, data: location }))
      : value
      ? { label: getLocalizedName(value, lang), value: value.id, data: value }
      : undefined;

    return (
      <>
        <LocationResolutionModal
          open={addLocationModalOpen}
          initialQuery={lastSearchQuery}
          onClose={() => setAddLocationModalOpen(false)}
          onResolved={handleAddLocation}
        />
        <Select<LocationModelOption | Array<LocationModelOption>, LocationModelOption>
          {...selectProps}
          ref={ref}
          mode={mode}
          status={props["aria-invalid"] === "true" ? "error" : undefined}
          size={size}
          allowClear={allowClear}
          showSearch={{
            filterOption: false,
            onSearch: (query) => {
              setLastSearchQuery(query);
              debounceFetcher(query);
            },
          }}
          style={{ width: "100%" }}
          labelInValue
          loading={fetching}
          notFoundContent={searchFailed ? <Typography.Text type="danger">{t("message_locations_load_reject")}</Typography.Text> : undefined}
          value={selectValue}
          onChange={handleChange}
          options={selectOptions}
          optionRender={(option) => {
            const localizedName = getLocalizedName(option.data.data, lang);
            const englishName = option.data.data.englishName?.trim();
            const label =
              lang !== "en" && englishName && englishName !== localizedName.trim() ? `${localizedName} / ${englishName}` : localizedName;
            return (
              <Flex gap={4} align="center">
                {getIconByLocationType(option.data.data.type)}
                <span title={label} style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {label}
                </span>
              </Flex>
            );
          }}
          placeholder={placeholder}
          prefix={!multiple && value ? <Flex>{getIconByLocationType(value.type)}</Flex> : null}
          maxTagCount={multiple ? "responsive" : undefined}
          disabled={disabled}
          popupRender={
            allowCreateNewLocation
              ? (menu) => (
                  <>
                    {menu}
                    <Divider style={{ margin: 0 }} />
                    <Flex justify="space-between" style={{ padding: "5px 12px" }} align="center">
                      <Typography.Text>{t("did_not_find_suitable_location")}</Typography.Text>
                      <Button type="primary" onClick={() => setAddLocationModalOpen(true)}>
                        {t("add")}
                      </Button>
                    </Flex>
                  </>
                )
              : undefined
          }
        />
      </>
    );
  }),
);

export default LocationPicker;
